/**
 * Wiederkehrende Aufgaben — reine Datumsberechnung.
 *
 * Kein Scheduler: die nächste Instanz entsteht beim Erledigen.
 * Kalenderdaten als YYYY-MM-DD in UTC.
 */
import { z } from "zod";
export const serienTypSchema = z.enum(["taeglich", "woechentlich", "monatlich"]);
export const serienRegelSchema = z
    .object({
    typ: serienTypSchema,
    intervall: z.number().int().min(1).max(52),
    /** JS-getDay: 0 = Sonntag … 6 = Samstag; nur bei woechentlich. */
    wochentage: z.array(z.number().int().min(0).max(6)).min(1).optional(),
})
    .superRefine((r, ctx) => {
    if (r.typ !== "woechentlich" && r.wochentage !== undefined) {
        ctx.addIssue({
            code: "custom",
            message: "Wochentage nur bei wöchentlicher Wiederholung.",
            path: ["wochentage"],
        });
    }
    if (r.wochentage && new Set(r.wochentage).size !== r.wochentage.length) {
        ctx.addIssue({
            code: "custom",
            message: "Wochentage ohne Duplikate.",
            path: ["wochentage"],
        });
    }
});
function pad2(n) {
    return String(n).padStart(2, "0");
}
function formatDate(y, m, d) {
    return `${y}-${pad2(m)}-${pad2(d)}`;
}
function parseDate(dateStr) {
    const [y, m, d] = dateStr.split("-").map(Number);
    return { y: y ?? 0, m: m ?? 1, d: d ?? 1 };
}
function addDays(dateStr, days) {
    const { y, m, d } = parseDate(dateStr);
    const dt = new Date(Date.UTC(y, m - 1, d + days));
    return formatDate(dt.getUTCFullYear(), dt.getUTCMonth() + 1, dt.getUTCDate());
}
function weekdayOf(dateStr) {
    const { y, m, d } = parseDate(dateStr);
    return new Date(Date.UTC(y, m - 1, d)).getUTCDay();
}
/**
 * Nächstes Fälligkeitsdatum ab `von` (exklusiv, wenn von selbst der Anker ist —
 * wir starten einen Tag nach dem Anker und suchen vorwärts).
 */
export function naechsteFaelligkeit(regel, von) {
    if (regel.typ === "taeglich") {
        return addDays(von, regel.intervall);
    }
    if (regel.typ === "woechentlich") {
        const tage = [...(regel.wochentage ?? [weekdayOf(von)])].sort((a, b) => a - b);
        let cursor = addDays(von, 1);
        // Maximal 2*intervall*7 Tage suchen
        for (let i = 0; i < regel.intervall * 14 + 7; i++) {
            const wd = weekdayOf(cursor);
            if (tage.includes(wd)) {
                // Zähle vollendete Wochen seit `von`
                const daysSince = daysBetween(von, cursor);
                const weeks = Math.floor((daysSince - 1) / 7);
                if (weeks % regel.intervall === 0 || daysSince <= 7) {
                    // erste passende Woche oder jede n-te
                    if (regel.intervall === 1 || weeks % regel.intervall === 0) {
                        return cursor;
                    }
                }
            }
            cursor = addDays(cursor, 1);
        }
        return addDays(von, 7 * regel.intervall);
    }
    // monatlich
    const { y, m, d } = parseDate(von);
    let nm = m + regel.intervall;
    let ny = y + Math.floor((nm - 1) / 12);
    nm = ((nm - 1) % 12) + 1;
    const maxDay = new Date(Date.UTC(ny, nm, 0)).getUTCDate();
    return formatDate(ny, nm, Math.min(d, maxDay));
}
function daysBetween(a, b) {
    const pa = parseDate(a);
    const pb = parseDate(b);
    const da = Date.UTC(pa.y, pa.m - 1, pa.d);
    const db = Date.UTC(pb.y, pb.m - 1, pb.d);
    return Math.round((db - da) / 86_400_000);
}
//# sourceMappingURL=serien.js.map